import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  DEFAULT_POLL_INTERVAL_S,
  ORG_POLL_INTERVAL_MAX_S,
  ORG_POLL_INTERVAL_MIN_S,
  ROLES,
  type Role,
} from '@seencast/shared';
import { useOrg, useTeam, useCreateInvite, useUpdateOrg, roleAlwaysOrgWide, type ScopedOrgMember } from '../../api/org.ts';
import { usePermissions } from '../../auth/session.tsx';
import { Button, Badge, Chip, Loading, SelectField, TextField } from '../../components/ui.tsx';
import { CopyField } from './CopyField.tsx';
import { ApiKeysSection } from './ApiKeysSection.tsx';
import { BrandSection } from './BrandSection.tsx';
import { SlackSection } from './SlackSection.tsx';
import { ReportSchedulesSection } from './ReportSchedulesSection.tsx';
import { LayoutsSection } from './LayoutsSection.tsx';
import { ActivitySection } from './ActivitySection.tsx';
import { MemberScopeDrawer } from './MemberScopeDrawer.tsx';
import { ScopePicker } from './ScopePicker.tsx';
import { scopeSummary, toScopeRefs } from './scopeUtils.ts';
import { formatMb, titleCase } from '../../lib/format.ts';
import { ApiError } from '../../api/client.ts';

// Roles an admin can grant (not owner — ownership transfer is a separate flow).
const ASSIGNABLE: Role[] = ROLES.filter((r) => r !== 'owner');

/** Client-side mirror of the server's PATCH /v1/org bound-check (shared constants), so an
 * out-of-range value disables Save instead of round-tripping to a 422. */
function isValidPollInterval(raw: string): boolean {
  const n = Number(raw);
  return Number.isInteger(n) && n >= ORG_POLL_INTERVAL_MIN_S && n <= ORG_POLL_INTERVAL_MAX_S;
}

export function OrgProfile() {
  const org = useOrg();
  const perms = usePermissions();
  const update = useUpdateOrg();
  const [name, setName] = useState('');
  const [pollIntervalS, setPollIntervalS] = useState('');

  useEffect(() => {
    if (org.data) {
      setName(org.data.name);
      setPollIntervalS(String(org.data.limits.poll_interval_s ?? DEFAULT_POLL_INTERVAL_S));
    }
  }, [org.data]);

  if (org.isLoading || !org.data) return <div className="card card-pad"><Loading /></div>;
  const { plan, limits, usage } = org.data;
  const currentPollIntervalS = limits.poll_interval_s ?? DEFAULT_POLL_INTERVAL_S;

  return (
    <div className="card card-pad">
      <div className="card-title">Organization</div>
      {perms.canManageOrg ? (
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div style={{ flex: 1, maxWidth: 360 }}>
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <Button
            onClick={() =>
              update.mutate(
                { name },
                {
                  onSuccess: () => toast.success('Saved'),
                  onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
                },
              )
            }
            disabled={!name.trim() || name === org.data.name || update.isPending}
          >
            Save
          </Button>
        </div>
      ) : (
        <dl className="dl">
          <dt>Name</dt>
          <dd>{org.data.name}</dd>
        </dl>
      )}

      <div className="divider" />
      <div className="card-title">Plan &amp; limits</div>
      <dl className="dl">
        <dt>Plan</dt>
        <dd>
          <Badge tone="info">{titleCase(plan)}</Badge>
        </dd>
        <dt>Screens</dt>
        <dd>
          {usage.screens} / {limits.max_screens}
        </dd>
        <dt>Storage</dt>
        <dd>
          {formatMb(usage.storage_mb)} / {formatMb(limits.max_storage_mb)}
        </dd>
        <dt>Assistant</dt>
        <dd>{limits.assistant ? <Badge tone="ok">Enabled</Badge> : <Badge tone="neutral">Off</Badge>}</dd>
      </dl>
      <p className="hint mt-2">Contact SeenLabs to change your plan or limits.</p>

      <div className="divider" />
      <div className="card-title">Player polling</div>
      {perms.canManageOrg ? (
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div style={{ flex: 1, maxWidth: 200 }}>
            <TextField
              label="Poll interval (seconds)"
              type="number"
              min={ORG_POLL_INTERVAL_MIN_S}
              max={ORG_POLL_INTERVAL_MAX_S}
              value={pollIntervalS}
              onChange={(e) => setPollIntervalS(e.target.value)}
              hint="How often screens check for updates when the live channel is down. Screens on the live channel already get changes instantly."
            />
          </div>
          <Button
            onClick={() =>
              update.mutate(
                { poll_interval_s: Number(pollIntervalS) },
                {
                  onSuccess: () => toast.success('Saved'),
                  onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
                },
              )
            }
            disabled={
              !isValidPollInterval(pollIntervalS) ||
              Number(pollIntervalS) === currentPollIntervalS ||
              update.isPending
            }
          >
            Save
          </Button>
        </div>
      ) : (
        <dl className="dl">
          <dt>Poll interval</dt>
          <dd>{currentPollIntervalS}s</dd>
        </dl>
      )}
    </div>
  );
}

/** Roster "Access" cell: "Full access" (owner/admin, no editing affordance),
 *  a muted "Org-wide" chip, or an accented "N screens · M groups" chip — plus an
 *  admin-only "Edit access" button for scopable (editor/viewer) rows. */
function AccessCell({
  member,
  canEdit,
  onEdit,
}: {
  member: ScopedOrgMember;
  canEdit: boolean;
  onEdit: () => void;
}) {
  if (roleAlwaysOrgWide(member.role)) {
    return <span className="text-dim">Full access</span>;
  }
  return (
    <div className="row" style={{ gap: 'var(--sp-2)' }}>
      <Chip selected={member.scopes !== null}>{scopeSummary(member.scopes)}</Chip>
      {canEdit && (
        <Button size="sm" onClick={onEdit}>
          Edit access
        </Button>
      )}
    </div>
  );
}

export function TeamSection() {
  const team = useTeam();
  const perms = usePermissions();
  const invite = useCreateInvite();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('editor');
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<ScopedOrgMember | null>(null);
  const [limitAccess, setLimitAccess] = useState(false);
  const [inviteScreens, setInviteScreens] = useState<Set<string>>(new Set());
  const [inviteGroups, setInviteGroups] = useState<Set<string>>(new Set());

  const toggleInviteScreen = (id: string) =>
    setInviteScreens((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleInviteGroup = (id: string) =>
    setInviteGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (team.isLoading || !team.data) return <div className="card card-pad"><Loading /></div>;

  return (
    <div className="card card-pad">
      <div className="card-title">Team</div>
      <div className="table-wrap mb-4">
        <table className="data">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Access</th>
            </tr>
          </thead>
          <tbody>
            {team.data.members.map((m) => (
              <tr key={m.user_id}>
                <td className="cell-strong">
                  {m.name} {m.is_you && <span className="text-dim">(you)</span>}
                </td>
                <td className="cell-dim">{m.email}</td>
                <td>
                  <Badge tone="neutral">{m.role}</Badge>
                </td>
                <td>
                  <AccessCell
                    member={m}
                    canEdit={perms.canManageTeam && !roleAlwaysOrgWide(m.role)}
                    onEdit={() => setEditingMember(m)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {team.data.invites.length > 0 && (
        <>
          <div className="card-title">Pending invites</div>
          {team.data.invites.map((inv) => (
            <div key={inv.id} className="mb-4">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span>
                  {inv.email} <Badge tone="neutral">{inv.role}</Badge>
                </span>
              </div>
              <div className="mt-2">
                <CopyField value={inv.invite_url} label={`Invite link for ${inv.email}`} />
              </div>
            </div>
          ))}
        </>
      )}

      {perms.canManageTeam && (
        <>
          <div className="divider" />
          <div className="card-title">Invite a teammate</div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!email.includes('@')) return;
              const scoped = limitAccess && role !== 'admin';
              invite.mutate(
                { email, role, ...(scoped ? { scopes: toScopeRefs(inviteScreens, inviteGroups) } : {}) },
                {
                  onSuccess: (res) => {
                    setLastInviteUrl(res.invite_url);
                    setEmail('');
                    setLimitAccess(false);
                    setInviteScreens(new Set());
                    setInviteGroups(new Set());
                    toast.success('Invite created — copy the link below');
                  },
                  onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
                },
              );
            }}
          >
            <div className="field-row" style={{ alignItems: 'flex-end' }}>
              <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" />
              <SelectField
                label="Role"
                value={role}
                onChange={(e) => {
                  const next = e.target.value as Role;
                  setRole(next);
                  if (next === 'admin') setLimitAccess(false);
                }}
              >
                {ASSIGNABLE.map((r) => (
                  <option key={r} value={r}>
                    {titleCase(r)}
                  </option>
                ))}
              </SelectField>
              <Button variant="primary" type="submit" disabled={!email.includes('@') || invite.isPending}>
                Invite
              </Button>
            </div>

            {role !== 'admin' && (
              <div className="mt-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => setLimitAccess((v) => !v)}>
                  {limitAccess ? 'Remove access limit' : 'Limit access'}
                </Button>
                {limitAccess && (
                  <div className="mt-2">
                    <ScopePicker
                      selectedScreens={inviteScreens}
                      selectedGroups={inviteGroups}
                      onToggleScreen={toggleInviteScreen}
                      onToggleGroup={toggleInviteGroup}
                    />
                  </div>
                )}
              </div>
            )}
          </form>
          {lastInviteUrl && (
            <div className="hint-box info mt-4">
              Share this link with the new teammate:
              <div className="mt-2">
                <CopyField value={lastInviteUrl} label="Invite link" />
              </div>
            </div>
          )}
        </>
      )}

      {editingMember && (
        <MemberScopeDrawer member={editingMember} onClose={() => setEditingMember(null)} />
      )}
    </div>
  );
}

export function SettingsPage() {
  const perms = usePermissions();
  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <div className="subtitle">Organization, team, and integration keys.</div>
        </div>
      </div>
      <div className="grid" style={{ gap: 'var(--sp-4)' }}>
        <OrgProfile />
        <BrandSection />
        <LayoutsSection />
        <TeamSection />
        <ApiKeysSection />
        <SlackSection />
        <ReportSchedulesSection />
        {/* Activity log — admin/owner only (hidden entirely otherwise). */}
        {perms.canManageTeam && <ActivitySection />}
      </div>
    </div>
  );
}
